/**
 * src/components/spatial/proximityGreeting.ts
 *
 * Which building, if any, should say hello because somebody walked up
 * to it.
 *
 * "May start the conversation" has been a setting in the presence
 * editor since the beginning and has never been read by anything: the
 * server does not push, and the client had no rule for when an object
 * should speak first. So an operator could switch it on, walk to their
 * building, and be met with silence by a thing configured to greet
 * them.
 *
 * This is the rule, kept pure so it can be argued with in a test rather
 * than by walking around Dubai. It decides *whether* and *which*; the
 * caller decides what to do about it.
 *
 * ## Why the client and not the server
 *
 * The visitor's position is on the client, the audio has to play in the
 * visitor's tab, and the browser will only allow that after the visitor
 * has interacted with the page. A server-pushed greeting would arrive
 * somewhere it cannot be heard. What the server keeps is authority: it
 * still applies the object's audience rules, execution rule, cooldown
 * and quotas when the conversation is actually opened, and may refuse.
 *
 * ## Why a ceiling on the radius
 *
 * The radius comes from the object's own configuration, which is
 * operator-supplied. Without a bound, one object with a 50 km radius
 * would greet everybody who opened the map and drown out every other
 * object in the city.
 */

import { haversineMeters, pointInPolygon, type LngLatLite } from '../../services/spatial/spatialProximity';
import type { SpatialObjectFeature } from '../../services/spatial/geoLinkTypes';

/** Used when an object is configured to greet but names no radius. */
export const DEFAULT_GREETING_RADIUS_M = 100;

/** No object may claim more than this, whatever its configuration says. */
export const MAX_GREETING_RADIUS_M = 500;

/** How long before the same object may greet the same visitor again. */
export const GREETING_COOLDOWN_MS = 15 * 60_000;

export interface GreetingTarget {
  objectId: string;
  name: string;
  accentColor: string;
  center: LngLatLite;
  distanceMeters: number;
}

export interface GreetingRules {
  now?: number;
  /** Object id → when it last greeted this visitor. */
  greetedAt?: Record<string, number>;
  cooldownMs?: number;
}

function bag(value: unknown): Record<string, unknown> {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) return value as Record<string, unknown>;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : {};
    } catch {
      return {};
    }
  }
  return {};
}

function text(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

/**
 * The radius this object greets within, bounded.
 *
 * Returns null when the object is not configured to greet at all,
 * which is the common case and the one worth being strict about: an
 * object that merely *talks* has not asked to start conversations.
 */
export function greetingRadiusFor(feature: SpatialObjectFeature): number | null {
  const meta = bag((feature.properties as Record<string, unknown>)?.metadata);
  const presence = bag(meta.ai_presence ?? meta.aiPresence);

  if (presence.enabled !== true) return null;
  if (presence.autoInitiate !== true) return null;
  // Passive is a contradiction with auto-initiate; the stricter setting
  // wins, because it is the one that says "do not speak to people".
  if (text(presence.mode ?? presence.interactionMode) === 'passive') return null;

  const configured = Number(presence.proximityRadiusMeters);
  const radius = Number.isFinite(configured) && configured > 0
    ? configured
    : DEFAULT_GREETING_RADIUS_M;
  return Math.min(radius, MAX_GREETING_RADIUS_M);
}

/** The point a distance is measured to: the centroid, else any vertex. */
export function featureCenter(feature: SpatialObjectFeature): LngLatLite | null {
  const props = (feature.properties || {}) as Record<string, unknown>;
  const lng = Number(props.centroidLng ?? props.lng);
  const lat = Number(props.centroidLat ?? props.lat);
  if (Number.isFinite(lng) && Number.isFinite(lat)) return { lng, lat };

  const walk = (coords: unknown): [number, number] | null => {
    if (!Array.isArray(coords)) return null;
    if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      return [coords[0] as number, coords[1] as number];
    }
    for (const inner of coords) {
      const found = walk(inner);
      if (found) return found;
    }
    return null;
  };
  const first = walk((feature.geometry as { coordinates?: unknown } | undefined)?.coordinates);
  return first ? { lng: first[0], lat: first[1] } : null;
}

/**
 * The nearest object that wants to greet this visitor, or null.
 *
 * Standing inside a building counts as zero distance rather than as
 * whatever its centroid happens to be from the doorway — for a tower
 * with a 100 m radius the difference decides whether it greets the
 * person in its lobby.
 */
export function pickProximityGreeting(
  features: readonly SpatialObjectFeature[],
  visitor: LngLatLite | null,
  rules: GreetingRules = {},
): GreetingTarget | null {
  if (!visitor || !Number.isFinite(visitor.lat) || !Number.isFinite(visitor.lng)) return null;

  const now = rules.now ?? Date.now();
  const cooldownMs = rules.cooldownMs ?? GREETING_COOLDOWN_MS;
  const greetedAt = rules.greetedAt ?? {};

  let best: GreetingTarget | null = null;

  for (const feature of features) {
    const props = (feature.properties || {}) as Record<string, unknown>;
    const objectId = text(props.objectId);
    if (!objectId) continue;

    const last = greetedAt[objectId];
    if (Number.isFinite(last) && now - Number(last) < cooldownMs) continue;

    const radius = greetingRadiusFor(feature);
    if (radius == null) continue;

    const center = featureCenter(feature);
    if (!center) continue;

    const inside = pointInPolygon(visitor, feature.geometry as { type?: string; coordinates?: unknown });
    const distanceMeters = inside ? 0 : haversineMeters(visitor, center);
    if (distanceMeters > radius) continue;

    if (!best || distanceMeters < best.distanceMeters) {
      best = {
        objectId,
        name: text(props.displayName) || text(props.name) || 'This place',
        accentColor: text(props.colorKey) || '#6366f1',
        center,
        distanceMeters,
      };
    }
  }

  return best;
}
